import { useState } from "react";
import {
    useInViewOnce,
} from "../../lib/utils/useAnimateOnVisible";

const DRAW_DURATION_MS = 1200;

function CompletionTrend({ trend }) {
    const [ref, inView] = useInViewOnce();
    const [clipId] = useState(
        () => `completion-trend-clip-${Math.random().toString(36).slice(2, 10)}`,
    );

    if (!trend || trend.length === 0) {
        return (
            <div className="text-gray-400 text-sm text-center py-4">
                No trend data available yet.
            </div>
        );
    }

    const padding = { top: 20, right: 40, bottom: 30, left: 40 };
    const width = 400;
    const height = 180;
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const points = trend.map((point, index) => {
        const x = padding.left + (trend.length > 1
            ? (index / (trend.length - 1)) * chartWidth
            : chartWidth / 2);
        const y = padding.top + chartHeight - (point.completion_percent / 100) * chartHeight;
        return { x, y, ...point };
    });

    const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');

    const lastPoint = points[points.length - 1];

    // Show ~4 evenly spaced date labels
    const labelCount = Math.min(4, trend.length);
    const labelIndices = [];
    if (trend.length <= labelCount) {
        for (let i = 0; i < trend.length; i++) labelIndices.push(i);
    } else {
        for (let i = 0; i < labelCount; i++) {
            labelIndices.push(Math.round(i * (trend.length - 1) / (labelCount - 1)));
        }
    }

    function formatDate(dateStr) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            return `${month}/${day}`;
        }
        return dateStr;
    }

    // Y-axis labels
    const yLabels = [0, 25, 50, 75, 100];

    const clipWidth = inView ? chartWidth : 0;

    return (
        <div ref={ref} className="w-full">
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="w-full h-auto"
                preserveAspectRatio="xMidYMid meet"
            >
                <defs>
                    <clipPath id={clipId}>
                        <rect
                            x={padding.left}
                            y={0}
                            width={clipWidth}
                            height={height}
                            style={{
                                transition: `width ${DRAW_DURATION_MS}ms ease-out`,
                            }}
                        />
                    </clipPath>
                </defs>

                {/* Y-axis gridlines and labels */}
                {yLabels.map(val => {
                    const y = padding.top + chartHeight - (val / 100) * chartHeight;
                    return (
                        <g key={val}>
                            <line
                                x1={padding.left}
                                y1={y}
                                x2={padding.left + chartWidth}
                                y2={y}
                                stroke="#e5e7eb"
                                strokeWidth="0.5"
                            />
                            <text
                                x={padding.left - 6}
                                y={y + 3}
                                textAnchor="end"
                                fontSize="9"
                                fill="#9ca3af"
                            >
                                {val}%
                            </text>
                        </g>
                    );
                })}

                {/* Animated area + line + dots (clipped to reveal left-to-right) */}
                <g clipPath={`url(#${clipId})`}>
                    {/* Area fill */}
                    <polygon
                        points={`${points[0].x},${padding.top + chartHeight} ${polylinePoints} ${lastPoint.x},${padding.top + chartHeight}`}
                        fill="#3b82f6"
                        fillOpacity="0.08"
                    />

                    {/* Line */}
                    <polyline
                        points={polylinePoints}
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />

                    {/* Data point dots */}
                    {points.length <= 15 && points.map((p, i) => (
                        <circle
                            key={i}
                            cx={p.x}
                            cy={p.y}
                            r="2.5"
                            fill="#3b82f6"
                        />
                    ))}
                </g>

                {/* End-of-line label (fades in after draw) */}
                <text
                    x={lastPoint.x + 6}
                    y={lastPoint.y + 4}
                    fontSize="11"
                    fontWeight="600"
                    fill="#3b82f6"
                    style={{
                        opacity: inView ? 1 : 0,
                        transition: `opacity 300ms ease-out ${DRAW_DURATION_MS - 150}ms`,
                    }}
                >
                    {lastPoint.completion_percent}%
                </text>

                {/* X-axis date labels */}
                {labelIndices.map(idx => {
                    const p = points[idx];
                    return (
                        <text
                            key={idx}
                            x={p.x}
                            y={padding.top + chartHeight + 16}
                            textAnchor="middle"
                            fontSize="9"
                            fill="#9ca3af"
                        >
                            {formatDate(p.date)}
                        </text>
                    );
                })}
            </svg>
        </div>
    );
}

export default CompletionTrend;
